/*
  Frame picker
  ------------
  Presents the combined "Frame" option as three controls (Frame, Frame colour,
  Mount) that all resolve to one value of the underlying native <select>.
  The select is the single source of truth: setting it and firing `change`
  lets the theme's <variant-selects> do everything it normally does.

  High-variant products (>250 variants)
  -------------------------------------
  Liquid only ever hands us the option values for the CURRENT subtree, and
  their `available` flags are only true for the selections in play right now.
  Every change therefore has to round-trip through the Section Rendering API
  before we know what is still buyable. Three consequences shape the code
  below:

    1. variant-selects swaps this markup out on every selection. Because this
       is a custom element, connectedCallback runs again on the fresh markup
       and the controls rebuild automatically. Anything that must outlive a
       selection lives in module scope or sessionStorage, never on `this`.
    2. Between the click and the response we know what the customer picked but
       not what is still available, so the pending build shows the new active
       state and withholds the availability marks rather than showing stale
       ones.
    3. Focus is restored by our own key, not by the theme. The theme refocuses
       `#targetId`, which for a dropdown is an <option> and so a no-op, and the
       pill the customer actually clicked no longer exists after the rebuild.
*/
(() => {
  if (customElements.get('frame-picker')) return;

  const MOUNT_SUFFIX = /\s*\+\s*Mount\s*$/i;
  const UNFRAMED = /^\s*unframed\s*$/i;
  const NOTE_TTL = 6000;
  const SETTLE_TIMEOUT = 5000;

  // Must survive the section re-render that replaces this element.
  let pendingFocusKey = null;
  // Last thing the customer focused anywhere in the option set. The repair
  // path fires a second selection they did not ask for, and the theme's own
  // restore aims at an <option>, so without this focus lands on <body>.
  // Ids survive the swap: viewTransition renames the OUTGOING node's ids.
  let lastFocusId = null;
  // Two listeners because neither alone is reliable. focusin misses the case
  // where clicking a radio's label operates it without ever focusing the
  // input, which Chrome does sometimes and our harness confirmed on the size
  // pills: the change event fired, the render ran, and focusin never came. A
  // change event always identifies the control that was operated, so it is
  // the primary signal; focusin still covers keyboard traversal, where a
  // control can gain focus without being changed.
  const recordLastControl = (e) => {
    const el = e.target;
    if (el && el.id && el.closest && el.closest('variant-selects') && !el.closest('frame-picker')) {
      lastFocusId = el.id;
    }
  };
  document.addEventListener('focusin', recordLastControl);
  document.addEventListener('change', recordLastControl);

  class FramePicker extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('.frame-picker__native');
      this.controls = this.querySelector('.frame-picker__controls');
      this.note = this.querySelector('.frame-picker__note');
      if (!this.select || !this.controls) return;
      // The pills are the interface once we upgrade. The select is only
      // clipped, so without this a screen reader announces Frame twice: once
      // as a 21-value combobox and again as the pills. Still submitted with
      // the form, and still the whole interface when JS never runs.
      this.select.setAttribute('aria-hidden', 'true');
      this.select.setAttribute('tabindex', '-1');
      this.busy = false;
      // Follows the theme's variant picker setting. Anything that is not
      // 'dropdown' keeps the original pill layout, so an unset value behaves as
      // it always did.
      this.mode = this.dataset.pickerType === 'dropdown' ? 'dropdown' : 'button';
      this.swatchShape = this.dataset.swatchShape || 'circle';
      this.showSwatches = this.swatchShape !== 'none';
      this.caretTemplate = this.querySelector('.frame-picker__caret-template');
      this.readSwatchMap();
      this.read();
      this.repairIfNeeded();
      this.build();
    }

    disconnectedCallback() {
      clearTimeout(this.settleTimer);
    }

    /* ── read the truth out of the native select ── */
    read() {
      this.entries = [...this.select.options].map((opt) => {
        const text = (opt.textContent || '').trim();
        const unavailable = opt.disabled || /unavailable|sold out/i.test(text);
        const value = opt.value;
        const isUnframed = UNFRAMED.test(value);
        const mounted = MOUNT_SUFFIX.test(value);
        const finish = isUnframed ? null : value.replace(MOUNT_SUFFIX, '').trim();
        // Set by product-variant-options when rendered as swatch_dropdown.
        // Already a CSS value: rgb(...) for a colour, url(...) for an image.
        const swatch = opt.dataset.optionSwatchValue || null;
        const focal = opt.dataset.optionSwatchFocalPoint || null;
        return { opt, value, unavailable, isUnframed, mounted, finish, swatch, focal };
      });
      this.finishes = [];
      for (const e of this.entries) {
        if (e.finish && !this.finishes.includes(e.finish)) this.finishes.push(e.finish);
      }
      this.current = this.entries.find((e) => e.value === this.select.value) || this.entries[0];
    }

    entryFor(finish, mounted) {
      return this.entries.find((e) => e.finish === finish && e.mounted === mounted);
    }
    unframedEntry() {
      return this.entries.find((e) => e.isUnframed);
    }
    // Colour metaobject entries keyed by display name, emitted by the snippet.
    // Only needed because Frame cannot carry a real swatch on these products.
    readSwatchMap() {
      this.swatchMap = {};
      const el = this.querySelector('.frame-picker__swatch-map');
      if (!el) return;
      try {
        const parsed = JSON.parse(el.textContent);
        this.swatchEntryCount = parsed.count || 0;
        // Frames now have their own metaobject library, so one label should
        // resolve to one entry and this preference should never be needed.
        // It is kept because it costs nothing and the failure it guards
        // against was real: while frames shared Shopify's Color list with the
        // print colours, a plain White for a mug sat next to White the frame,
        // and letting document order decide gave the picker a flat white
        // circle instead of the frame photo. An image beats a flat colour, and
        // a frame photo beats any other image.
        const score = (entry) =>
          entry.image ? (/\/frame-/i.test(entry.value) ? 2 : 1) : 0;
        (parsed.entries || []).forEach((entry) => {
          if (!entry || !entry.label) return;
          const existing = this.swatchMap[entry.label];
          if (existing && existing.score >= score(entry)) return;
          this.swatchMap[entry.label] = { value: entry.value, score: score(entry) };
        });
      } catch (e) {}
    }

    // Case-insensitive so "Dark green" still finds an entry named "Dark Green".
    mappedSwatch(name) {
      if (!name) return null;
      const direct = this.swatchMap[name];
      if (direct) return direct;
      const key = Object.keys(this.swatchMap).find(
        (k) => k.toLowerCase() === String(name).toLowerCase()
      );
      return key ? this.swatchMap[key] : null;
    }

    // A finish's swatch lives on its option values. Prefer the unmounted one,
    // since the two are the same frame, and fall back so a finish that only
    // exists mounted still gets its swatch. If the option carries no swatch at
    // all, fall back to the colour metaobject of the same name.
    swatchFor(finish) {
      const e = this.entryFor(finish, false) || this.entryFor(finish, true);
      if (e && e.swatch) return { value: e.swatch, focal: e.focal };
      const mapped = this.mappedSwatch(finish);
      return mapped && mapped.value ? { value: mapped.value, focal: null } : null;
    }
    finishUnavailable(finish) {
      const plain = this.entryFor(finish, false);
      const mount = this.entryFor(finish, true);
      return (!plain || plain.unavailable) && (!mount || mount.unavailable);
    }

    /* ── size changed underneath us and the old choice no longer exists ── */
    repairIfNeeded() {
      if (!this.current || !this.current.unavailable) return;
      const un = this.unframedEntry();
      if (this.current.isUnframed || !un || un.unavailable) return;
      // e.g. A1 selected while a framed value was chosen: fall back and say so.
      // The note must survive the section re-render that follows, so park it
      // in sessionStorage rather than on this soon-to-be-replaced element.
      this.writeNote('That size is unframed only, so the frame was reset.');
      this.setValue(un.value, { keepNote: true });
    }

    /* ── build the controls ──
       One description of the choices, two ways of drawing it. Everything that
       decides what a click resolves to lives in rows(); pillRow and dropdownRow
       only render. Keeping the two modes off one description is what stops the
       availability and repair behaviour drifting apart between them. */
    build() {
      const c = this.controls;
      c.hidden = false;
      c.innerHTML = '';
      const draw = this.mode === 'dropdown' ? (r) => this.dropdownRow(r) : (r) => this.pillRow(r);
      this.rows().forEach((r) => c.appendChild(draw(r)));
      this.renderNote();
      this.restoreFocus();
    }

    /* Each item names the option value it resolves to, so neither renderer has
       to know how a frame, a colour and a mount combine. A null value means
       there is no such combination at all, which is different from one that
       exists but is currently unavailable. */
    rows() {
      const cur = this.current;
      const framed = cur && !cur.isUnframed;
      const un = this.unframedEntry();
      const rows = [];

      // Prefer somewhere buyable, but still go somewhere if nothing is: the
      // server decides, and the customer can pivot size from there.
      const firstFramed = () => {
        const candidates = this.finishes.map(
          (f) => this.entryFor(f, false) || this.entryFor(f, true)
        );
        return candidates.find((e) => e && !e.unavailable) || candidates.find(Boolean);
      };

      // Keep the mount state when switching finish, falling back so a finish
      // that only exists one way round is still reachable.
      const finishItem = (f) => {
        const e = this.entryFor(f, cur && cur.mounted) || this.entryFor(f, !(cur && cur.mounted));
        return {
          text: f,
          key: `colour:${f}`,
          value: e ? e.value : null,
          active: !!cur && cur.finish === f,
          unavailable: this.finishUnavailable(f),
          swatch: this.swatchFor(f),
        };
      };

      if (this.mode === 'dropdown') {
        // Unframed sits in the same list as the finishes: one control answers
        // "which frame", the way the reference storefront does it.
        rows.push({
          label: 'Frame',
          key: 'frame',
          items: [
            {
              text: 'Unframed',
              key: 'frame:unframed',
              value: un ? un.value : null,
              active: !framed,
              unavailable: un ? un.unavailable : true,
            },
            ...this.finishes.map(finishItem),
          ],
        });
      } else {
        const anyFramedAvailable = this.finishes.some((f) => !this.finishUnavailable(f));
        const target = firstFramed();
        rows.push({
          label: 'Frame',
          key: 'frame',
          items: [
            {
              text: 'Unframed',
              key: 'frame:unframed',
              value: un ? un.value : null,
              active: !framed,
              unavailable: un ? un.unavailable : true,
            },
            {
              text: 'Framed',
              key: 'frame:framed',
              value: target ? target.value : null,
              active: !!framed,
              unavailable: !anyFramedAvailable,
            },
          ],
        });
        if (framed) {
          rows.push({
            label: 'Frame colour',
            key: 'colour',
            items: this.finishes.map(finishItem),
          });
        }
      }

      // Mount is its own control in both modes, and only once a frame is chosen.
      if (framed) {
        const noMount = this.entryFor(cur.finish, false);
        const yesMount = this.entryFor(cur.finish, true);
        rows.push({
          label: 'Mount',
          key: 'mount',
          items: [
            // "Without mount" rather than "No": as a pill it has to read on its
            // own, away from the row label, and Yes/No answers a question the
            // customer has not been asked.
            {
              text: 'Without mount',
              key: 'mount:no',
              value: noMount ? noMount.value : null,
              active: !cur.mounted,
              unavailable: !noMount || noMount.unavailable,
            },
            {
              text: 'With mount',
              key: 'mount:yes',
              value: yesMount ? yesMount.value : null,
              active: !!cur.mounted,
              unavailable: !yesMount || yesMount.unavailable,
            },
          ],
        });
      }

      return rows;
    }

    pillRow(spec) {
      const isSwatchRow = spec.items.some((item) => item.swatch);
      const chosen = spec.items.find((item) => item.active);
      return this.row(
        spec.label,
        spec.items.map((item) =>
          this.pill(
            item.text,
            item.key,
            item.active,
            item.unavailable,
            () => {
              if (item.value != null) this.setValue(item.value, { focusKey: item.key });
            },
            item.swatch
          )
        ),
        isSwatchRow && chosen ? chosen.text : null
      );
    }

    /* Mirrors the theme's own dropdown markup (label, .select wrapper, swatch,
       caret) so these sit flush with the stock Size and Design colour controls
       rather than looking like a bolt-on. */
    dropdownRow(spec) {
      const wrap = document.createElement('div');
      wrap.className = 'frame-picker__row frame-picker__row--dropdown';

      const id = `FramePicker-${this.dataset.section || 'x'}-${spec.key}`;
      const label = document.createElement('label');
      label.className = 'form__label';
      label.setAttribute('for', id);
      label.textContent = spec.label;
      wrap.appendChild(label);

      const shell = document.createElement('div');
      shell.className = 'select';

      const active = spec.items.find((i) => i.active) || spec.items[0];
      if (this.showSwatches && spec.items.some((i) => i.swatch)) {
        const holder = document.createElement('span');
        holder.className = 'dropdown-swatch';
        holder.appendChild(this.swatchChip(active && active.swatch));
        shell.appendChild(holder);
      }

      const select = document.createElement('select');
      select.className = 'select__select';
      select.id = id;
      select.dataset.fpKey = spec.key;

      for (const item of spec.items) {
        const opt = document.createElement('option');
        // A combination that does not exist cannot be a destination, so it is
        // disabled rather than offered and silently ignored.
        if (item.value == null) opt.disabled = true;
        opt.value = item.value == null ? '' : item.value;
        // While a selection is in flight the availability flags still describe
        // the PREVIOUS subtree, so showing them would be a lie.
        const showUnavailable = item.unavailable && !this.busy;
        opt.textContent = showUnavailable ? `${item.text} (unavailable)` : item.text;
        if (item.active) opt.setAttribute('selected', 'selected');
        select.appendChild(opt);
      }
      if (active && active.value != null) select.value = active.value;

      select.addEventListener('change', () => {
        if (select.value) this.setValue(select.value, { focusKey: spec.key });
      });
      shell.appendChild(select);

      if (this.caretTemplate) {
        shell.appendChild(this.caretTemplate.content.cloneNode(true));
      }

      wrap.appendChild(shell);
      return wrap;
    }

    // Same element and custom properties the theme's own swatch snippet uses,
    // so a swatch here looks like a swatch anywhere else on the store.
    swatchChip(swatch) {
      const chip = document.createElement('span');
      chip.className = 'swatch' + (this.swatchShape === 'square' ? ' swatch--square' : '');
      chip.setAttribute('aria-hidden', 'true');
      if (swatch) {
        chip.style.setProperty('--swatch--background', swatch.value);
        if (swatch.focal) chip.style.setProperty('--swatch-focal-point', swatch.focal);
      } else {
        chip.classList.add('swatch--unavailable');
      }
      return chip;
    }

    row(label, pills, selectedText) {
      const row = document.createElement('div');
      row.className = 'frame-picker__row';
      const l = document.createElement('span');
      l.className = 'frame-picker__sublabel';
      // "Frame colour: Oak", the same shape the theme's swatch picker uses for
      // Design colour. Only where the choice is a swatch: on a text pill row
      // the chosen pill already says the word, so repeating it in the label
      // would be noise.
      if (selectedText) {
        l.appendChild(document.createTextNode(`${label}: `));
        const value = document.createElement('span');
        value.setAttribute('data-selected-value', '');
        value.textContent = selectedText;
        l.appendChild(value);
      } else {
        l.textContent = label;
      }
      row.appendChild(l);
      const holder = document.createElement('div');
      // A row of swatches carries its own spacing, so it opts out of the pill
      // row's inline margins rather than getting both.
      const allSwatches =
        pills.length > 0 && pills.every((p) => p.classList.contains('frame-picker__pill--swatch'));
      holder.className = 'frame-picker__pills' + (allSwatches ? ' frame-picker__pills--swatch' : '');
      pills.forEach((p) => holder.appendChild(p));
      row.appendChild(holder);
      return row;
    }

    pill(text, key, active, unavailable, onClick, swatch) {
      const b = document.createElement('button');
      b.type = 'button';
      b.dataset.fpKey = key;
      // While a selection is in flight the availability flags still describe
      // the PREVIOUS subtree, so showing them would be a lie. Withhold the
      // mark until the section comes back rather than flashing a wrong one.
      const showUnavailable = unavailable && !this.busy;
      // A swatch says the colour better than the word does, so where there is
      // one the pill becomes the swatch and the name moves into the accessible
      // label. Matches how the theme renders Design colour.
      const swatchOnly = !!swatch;
      b.className =
        'frame-picker__pill' +
        (swatchOnly ? ' frame-picker__pill--swatch' : '') +
        (active ? ' is-active' : '') +
        (showUnavailable ? ' is-unavailable' : '');
      // The outline says which one is chosen visually; this says it to a
      // screen reader. Without it every pill announces as a plain button and
      // the current selection is unknowable by ear.
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (swatchOnly) {
        b.appendChild(this.swatchChip(swatch));
        b.setAttribute('aria-label', text);
        b.title = text;
      } else {
        b.textContent = text;
      }
      if (showUnavailable) {
        // Selectable, not a dead end: this matches how the theme's own size
        // picker behaves, and with 21 frame values a dead end would strand
        // anyone who happened to pick their size first.
        b.title = `${text} (not available with the current selection)`;
        b.setAttribute('aria-label', `${text} (not available with the current selection)`);
      }
      b.addEventListener('click', onClick);
      return b;
    }

    /* ── write the truth back and let the theme react ── */
    setValue(value, { keepNote = false, focusKey = null } = {}) {
      if (!keepNote) this.clearNote();

      const target = [...this.select.options].find((o) => o.value === value);
      if (!target) return;

      if (focusKey) pendingFocusKey = focusKey;

      if (this.select.value === value) {
        // Already here; nothing for the server to do.
        this.read();
        this.build();
        return;
      }

      // Set the live selection. VariantSelects synchronises the selected
      // attributes when it handles change, preserving this choice in Safari
      // even when Liquid did not mark an option as selected.
      this.select.value = value;

      this.setBusy(true);
      this.select.dispatchEvent(new Event('change', { bubbles: true }));

      // Reflect the new choice straight away. The section re-render replaces
      // this element with the authoritative markup a moment later; if the
      // fetch fails it never arrives, so drop out of the busy state on a timer.
      this.read();
      this.build();
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => {
        if (!this.isConnected) return;
        this.setBusy(false);
        this.read();
        this.build();
      }, SETTLE_TIMEOUT);
    }

    setBusy(on) {
      this.busy = on;
      this.classList.toggle('is-busy', on);
      this.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    restoreFocus() {
      if (pendingFocusKey) {
        const el = this.controls.querySelector(`[data-fp-key="${CSS.escape(pendingFocusKey)}"]`);
        if (el && document.activeElement !== el) el.focus();
        // Hold the key across the pending build so the post-re-render markup
        // can claim it too; only the settled build consumes it.
        if (!this.busy) pendingFocusKey = null;
        return;
      }
      // Nothing of ours was clicked, so this is the repair path: the customer
      // changed size and we changed the frame on their behalf. Put focus back
      // where they left it rather than stealing it to a control they did not
      // touch.
      if (this.busy || !lastFocusId) return;
      this.rescueFocus();
    }

    // Focus is not dropped when the new markup arrives. viewTransition leaves
    // the outgoing node in place, still holding focus, and hides and removes
    // it later. How much later is not a constant: a fixed 800ms window built
    // on one measurement expired before the drop on the repair path, where
    // instrumentation put the drop over a second after this ran. So listen
    // for the drop instead of guessing its timing: a focusout that settles on
    // <body> is the old node being taken out, and that is the moment to
    // reclaim. A pointerdown cancels the watch, because after a real click
    // focus belongs wherever the customer put it, including nowhere.
    rescueFocus() {
      const id = lastFocusId;
      if (!id) return;
      const reclaim = () => {
        const ae = document.activeElement;
        if (ae && ae !== document.body && ae !== document.documentElement) return false;
        document.getElementById(id)?.focus();
        return true;
      };
      if (reclaim()) return;
      const onDrop = () => {
        // focusout fires before focus settles; let it land, then look.
        setTimeout(() => {
          if (reclaim()) stop();
        }, 0);
      };
      const stop = () => {
        document.removeEventListener('focusout', onDrop);
        document.removeEventListener('pointerdown', stop);
        clearTimeout(cap);
      };
      document.addEventListener('focusout', onDrop);
      document.addEventListener('pointerdown', stop);
      // Backstop so the listeners cannot outlive the moment. Generous on
      // purpose: it only bounds cleanup, nothing waits on it.
      const cap = setTimeout(stop, 4000);
    }

    /* ── the reset note, which has to outlive a section re-render ── */
    get noteKey() {
      return `frame-picker-note:${this.dataset.section || 'default'}`;
    }

    writeNote(msg) {
      try {
        sessionStorage.setItem(this.noteKey, JSON.stringify({ msg, ts: Date.now() }));
      } catch (e) {}
    }

    clearNote() {
      try {
        sessionStorage.removeItem(this.noteKey);
      } catch (e) {}
    }

    renderNote() {
      if (!this.note) return;
      // Lives for a short window rather than being one-shot: it has to survive
      // both the synchronous rebuild and the async section morph that follows.
      let msg = null;
      try {
        const raw = sessionStorage.getItem(this.noteKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Date.now() - parsed.ts < NOTE_TTL) msg = parsed.msg;
          else this.clearNote();
        }
      } catch (e) {}
      this.note.textContent = msg || '';
      this.note.hidden = !msg;
    }
  }

  customElements.define('frame-picker', FramePicker);
})();
